import React, { useState, useRef, useEffect } from 'react';

interface CarouselProps {
  items: Array<{
    componentType: string;
    content: unknown;
  }>;
  handlers?: {
    onOptionSelect?: (option: string) => void;
    onCtaClick?: (offer: unknown) => void;
    onSendMessage?: (content: string) => void;
  };
  viewMode?: 'widget' | 'fullscreen';
  carouselId?: string; // Add optional carousel ID
}

const Carousel: React.FC<CarouselProps> = ({ 
  items, 
  handlers, 
  viewMode = 'widget',
  carouselId 
}) => {
  console.log('Carousel component rendered with:', { items, viewMode, handlersCount: Object.keys(handlers || {}).length });
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(1);
  const [cardWidth, setCardWidth] = useState(320);
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  // Removed drag/touch state variables to prevent dancing behavior
  
  // Generate unique ID for this carousel instance if not provided
  const instanceId = carouselId || `carousel-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

  // State to track if height has been calculated to prevent recalculation
  const heightCalculatedRef = useRef(false);
  const calculatedHeightRef = useRef<number>(450);

  // Calculate height only once when carousel loads
  useEffect(() => {
    // Prevent multiple calculations
    if (heightCalculatedRef.current || !items || items.length === 0) return;

    const calculateHeightOnce = () => {
      if (!carouselRef.current) return;

      // Mark as calculating to prevent other calls
      heightCalculatedRef.current = true;

      // Get all card elements from this specific carousel
      const cardElements = carouselRef.current.querySelectorAll('.carousel__slide > *');
      let maxHeight = 450; // Default minimum height

      if (cardElements.length === 0) {
        calculatedHeightRef.current = maxHeight;
        return;
      }

      // Measure each card's natural height
      cardElements.forEach((card) => {
        const element = card as HTMLElement;
        const rect = element.getBoundingClientRect();
        if (rect.height > 0) {
          maxHeight = Math.max(maxHeight, rect.height);
        }
      });

      // Add padding and store final height
      const calculatedHeight = maxHeight + 40;
      calculatedHeightRef.current = calculatedHeight;

      // Set CSS properties only once
      if (carouselRef.current) {
        carouselRef.current.style.setProperty('--carousel-card-width', '330px');
        carouselRef.current.style.setProperty('--carousel-card-height', `${calculatedHeight}px`);
      }

      console.log(`Height calculated once for carousel ${instanceId}:`, { calculatedHeight, cardCount: cardElements.length });
    };

    // Calculate after a delay to ensure all content is rendered
    const timeoutId = setTimeout(calculateHeightOnce, 600);
    return () => clearTimeout(timeoutId);
  }, [items, instanceId]); // Only run when items change, but ref prevents recalculation

  // Calculate visible slides based on container width and view mode
  useEffect(() => {
    const calculateVisibleSlides = () => {
      if (!containerRef.current) return;
      
      const containerWidth = containerRef.current.offsetWidth;
      
      if (viewMode === 'widget') {
        setVisibleSlides(1);
        setCardWidth(330); // Fixed width for widget mode
      } else {
        // For fullscreen, use fixed 330px width for all card types
        const fixedCardWidth = 330;
        setCardWidth(fixedCardWidth);
        
        const gap = 16; // Gap between slides
        const slidesPadding = 120; // 60px padding on each side of slides container
        const availableWidth = containerWidth - slidesPadding;
        
        // Calculate how many 330px cards + gaps can fit
        const slidesPerView = Math.floor((availableWidth + gap) / (fixedCardWidth + gap));
        setVisibleSlides(Math.max(1, Math.min(slidesPerView, items.length)));
        
        console.log('Carousel layout calculation:', {
          containerWidth,
          availableWidth,
          fixedCardWidth,
          gap,
          slidesPadding,
          calculatedSlidesPerView: slidesPerView
        });
      }
    };

    // Use setTimeout to avoid frequent recalculations
    const timeoutId = setTimeout(() => {
      calculateVisibleSlides();
    }, 100);
    
    // Add resize listener with debouncing
    const handleResize = () => {
      clearTimeout(timeoutId);
      setTimeout(calculateVisibleSlides, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [viewMode, items]); // Include items to satisfy ESLint, debouncing prevents excessive re-renders

  // Import component registry dynamically to avoid circular imports
  const [ComponentRegistry, setComponentRegistry] = useState<Record<string, React.ComponentType<unknown>>>({});
  const [getComponentProps, setGetComponentProps] = useState<((componentType: string, content: unknown, handlers?: unknown, isCarousel?: boolean) => unknown) | null>(null);

  useEffect(() => {
    import('./index').then(module => {
      setComponentRegistry(module.ComponentRegistry);
      setGetComponentProps(() => module.getComponentProps);
    });
  }, []);

  const maxSlides = Math.max(0, items.length - visibleSlides);

  const nextSlide = () => {
    setCurrentSlide(prev => {
      const newSlide = Math.min(prev + 1, maxSlides);
      return newSlide;
    });
  };

  const prevSlide = () => {
    setCurrentSlide(prev => {
      const newSlide = Math.max(prev - 1, 0);
      return newSlide;
    });
  };

  const goToSlide = (index: number) => {
    const targetSlide = Math.max(0, Math.min(index, maxSlides));
    setCurrentSlide(targetSlide);
  };

  // Touch/Mouse handlers for swipe functionality - disabled to prevent dancing
  // TODO: Re-implement stable swipe handling if needed

  const renderNavButton = (direction: 'prev' | 'next', onClick: () => void, disabled: boolean) => (
    <button
      className={`carousel__nav carousel__nav--${direction} ${disabled ? 'carousel__nav--disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? 'Previous slide' : 'Next slide'}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        {direction === 'prev' ? (
          <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        ) : (
          <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        )}
      </svg>
    </button>
  );

  if (!items || items.length === 0) {
    console.log('Carousel: No items to display', { items });
    return <div className="carousel-error" style={{padding: '20px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px'}}>No items to display (Debug: {JSON.stringify(items)})</div>;
  }

  console.log('Carousel: Rendering with items:', items);

  return (
    <div 
      ref={carouselRef} 
      className={`carousel carousel--${viewMode} ${items.length > visibleSlides ? 'carousel--has-multiple' : ''}`}
      data-carousel-id={instanceId}
    >
      <div className="carousel__container">
        {/* Navigation buttons - show only if more slides than visible */}
        {items.length > visibleSlides && (
          <>
            {renderNavButton('prev', prevSlide, currentSlide === 0)}
            {renderNavButton('next', nextSlide, currentSlide >= maxSlides)}
          </>
        )}

        {/* Slides container */}
        <div 
          ref={containerRef}
          className="carousel__slides"
          style={{
            transform: `translateX(-${currentSlide * (330 + 16)}px)`, // Fixed width + gap
          }}
        >
          {items.map((item, index) => {
            const Component = ComponentRegistry[item.componentType];
            
            if (!Component || !getComponentProps) {
              return (
                <div key={index} className="carousel__slide">
                  <div className="carousel__error">
                    Component "{item.componentType}" not found
                  </div>
                </div>
              );
            }

            const props = getComponentProps(item.componentType, item.content, handlers, true); // In carousel

            return (
              <div 
                key={index} 
                className="carousel__slide"
                style={{ 
                  width: viewMode === 'widget' ? '100%' : `${cardWidth}px`,
                  flexShrink: 0
                }}
              >
                <Component {...(props as Record<string, unknown>)} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots indicator - show only if more slides than visible */}
      {items.length > visibleSlides && (
        <div className="carousel__dots">
          {Array.from({ length: maxSlides + 1 }, (_, index) => (
            <button
              key={index}
              className={`carousel__dot ${index === currentSlide ? 'carousel__dot--active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
